import { get_depret_from_result } from "./LDSHelpers";
import translateLDSDestinations from "../services/translateLDSDestinations";
import { resolveBoardBasisForPackageSummary } from "./resolveBoardBasisForPackageSummary";
import { resolveOrganizedTourCardImage } from "./resolveOrganizedTourCardImage";
import { buildOrganizedTourProfileViewModel } from "./buildOrganizedTourProfileViewModel";
import { upgradeOrganizedTourRemoteImageUrl } from "./organizedTourHeroImageSsot";

function normStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

function parseExtendedList(order) {
  const raw = order?.extended;
  if (raw == null) return [];
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (Array.isArray(v)) return v.filter(Boolean);
  if (v && typeof v === "object") return [v];
  return [];
}

function absolutizeImageUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    try {
      const protocol =
        typeof window !== "undefined" && window.location?.protocol
          ? window.location.protocol
          : "https:";
      return `${protocol}${trimmed}`;
    } catch {
      return `https:${trimmed}`;
    }
  }
  if (trimmed.startsWith("/")) {
    try {
      if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}${trimmed}`;
      }
    } catch {
      /* ignore */
    }
  }
  return trimmed;
}

function firstImageFromObject(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const k of keys) {
    const u = absolutizeImageUrl(obj[k]);
    if (u) return u;
  }
  return null;
}

function readOurPriceOrganizedTourSnapshotUrl(order) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const raw =
    op.organized_tour_image_url ??
    op.organizedTourImageUrl ??
    "";
  return absolutizeImageUrl(raw);
}

/**
 * Raw row for the same SSOT resolvers as profile/cards when `selectedResult` is absent (loaded order only).
 */
export function buildOrganizedTourRawDealForImageResolve(order, selectedResult) {
  if (selectedResult && typeof selectedResult === "object") {
    return selectedResult;
  }
  const deal = order?.deal && typeof order.deal === "object" ? order.deal : null;
  const extFirst = parseExtendedList(order)[0] || null;
  const reservation = order?.order_response?.reservation || {};
  const tour =
    deal?.tour ||
    order?.tour ||
    extFirst?.tour ||
    reservation?.tour ||
    {};
  const hotel =
    order?.hotel ||
    reservation?.hotel ||
    deal?.hotel ||
    null;
  const linkedFlights =
    order?.linkedFlights || extFirst?.linkedFlights || deal?.linkedFlights || null;

  return {
    ...(deal || {}),
    fullDeal: deal || undefined,
    tour,
    hotel,
    linkedFlights,
    order_response: order?.order_response,
  };
}

/**
 * Same hero priority as organized-tour profile (`resourceUrl` + normalize + destination tiers), for persisting a URL snapshot on create/update.
 *
 * @param {{ selectedResult: object|null|undefined, resourceUrl?: string|null }} params
 * @returns {{ url: string | null, source: string }}
 */
export function resolveOrganizedTourHeroImageForPersist({ selectedResult, resourceUrl }) {
  if (!selectedResult || typeof selectedResult !== "object") {
    return { url: null, source: "missing" };
  }
  const vm = buildOrganizedTourProfileViewModel({
    selectedResult,
    resourceUrl: resourceUrl ?? null,
    ourPrice: null,
    savedOrder: null,
  });
  const hero = vm.heroImage && String(vm.heroImage).trim();
  if (hero) {
    return { url: hero, source: String(vm.imageSource || "profile_ssot") };
  }
  return { url: null, source: "missing" };
}

/**
 * Priority: ourprice snapshot → extended → deal / tour → order_response → card SSOT → profile VM SSOT (aligned with organized-tour booking UI).
 *
 * @param {object|null} order
 * @param {object|null|undefined} [selectedResult] — When present (same session), same row as the profile/cards.
 * @returns {{ url: string | null, source: string }}
 */
export function resolveOrganizedTourSuccessHeroImage(order, selectedResult) {
  if (!order || typeof order !== "object") {
    return { url: null, source: "missing" };
  }

  const snap = readOurPriceOrganizedTourSnapshotUrl(order);
  if (snap) return { url: snap, source: "ourprice.organized_tour_image_url" };

  const extList = parseExtendedList(order);
  for (let i = 0; i < extList.length; i++) {
    const row = extList[i];
    const fromImage = firstImageFromObject(row, ["image", "Image", "IMAGE"]);
    if (fromImage) return { url: fromImage, source: "extended.image" };
    const fromMain = firstImageFromObject(row, ["mainImage", "main_image", "MainImage"]);
    if (fromMain) return { url: fromMain, source: "extended.mainImage" };
  }
  const deal = order.deal && typeof order.deal === "object" ? order.deal : null;
  if (deal) {
    const dealTour = deal.tour && typeof deal.tour === "object" ? deal.tour : null;
    if (dealTour) {
      const fromTour = firstImageFromObject(dealTour, [
        "resourceUrl",
        "imageUrl",
        "image_url",
        "image",
        "mainImage",
        "main_image",
      ]);
      if (fromTour) return { url: fromTour, source: "deal.tour.resourceUrl" };
    }
    const u = firstImageFromObject(deal, [
      "image",
      "mainImage",
      "main_image",
      "imageUrl",
      "image_url",
      "destinationImage",
      "dealImage",
      "resourceUrl",
      "heroImage",
    ]);
    if (u) return { url: u, source: "deal.image" };
  }
  const resp = order.order_response;
  if (resp && typeof resp === "object") {
    const resTour = resp.tour && typeof resp.tour === "object" ? resp.tour : null;
    if (resTour) {
      const fromResTour = firstImageFromObject(resTour, [
        "resourceUrl",
        "imageUrl",
        "image",
        "mainImage",
      ]);
      if (fromResTour) return { url: fromResTour, source: "order_response.tour" };
    }
    const u =
      firstImageFromObject(resp, ["image", "mainImage", "heroImage", "imageUrl"]) ||
      firstImageFromObject(resp.reservation || {}, ["image", "mainImage"]) ||
      firstImageFromObject(resp.hotel || {}, [
        "image",
        "hotelImage",
        "mainImage",
        "resourceUrl",
      ]) ||
      firstImageFromObject(resp.tour || {}, ["image", "mainImage"]);
    if (u) return { url: u, source: "order_response" };
  }
  const raw = buildOrganizedTourRawDealForImageResolve(order, selectedResult);
  const card = resolveOrganizedTourCardImage(raw);
  if (card.resolvedImage) {
    const up = upgradeOrganizedTourRemoteImageUrl(String(card.resolvedImage).trim());
    const finalUrl = absolutizeImageUrl(up) || up;
    if (finalUrl) {
      return { url: finalUrl, source: `resolveOrganizedTourCardImage:${card.imageSource}` };
    }
  }

  const vm = buildOrganizedTourProfileViewModel({
    selectedResult: raw,
    resourceUrl: null,
    ourPrice: null,
    savedOrder: null,
  });
  if (vm.heroImage && String(vm.heroImage).trim()) {
    return {
      url: String(vm.heroImage).trim(),
      source: `profile_ssot:${vm.imageSource}`,
    };
  }

  return { url: null, source: "missing" };
}

export function orderDealtypeIsOrganizedTour(order) {
  const d = order?.dealtype ?? order?.dealType ?? order?.deal_type;
  return normStr(d).replace(/\s+/g, "") === "OrganizedTour";
}

function hasMeaningfulHotelSnapshot(order, resHotel) {
  const h = resHotel || order?.hotel;
  if (!h || typeof h !== "object") return false;
  const name = normStr(h.hotelName || h.name || h.HotelName);
  if (!name || name === "-") return false;
  return true;
}

function countPaxRooms(order) {
  const rooms = order?.passengersinfo ?? order?.passengers_info ?? order?.passengersInfo ?? [];
  if (!Array.isArray(rooms)) return { adults: 0, kids: 0, infants: 0 };
  let adults = 0;
  let kids = 0;
  let infants = 0;
  for (const r of rooms) {
    if (!r || typeof r !== "object") continue;
    adults += Array.isArray(r.adults) ? r.adults.length : 0;
    kids += Array.isArray(r.kids) ? r.kids.length : 0;
    infants += Array.isArray(r.infants) ? r.infants.length : 0;
  }
  return { adults, kids, infants };
}

function formatPaxSummary({ adults, kids, infants }) {
  const parts = [];
  if (adults > 0) parts.push(`${adults} מבוגרים`);
  if (kids > 0) parts.push(`${kids} ילדים`);
  if (infants > 0) parts.push(`${infants} תינוקות`);
  return parts.length ? parts.join(", ") : "—";
}

function nightsBetween(depStr, retStr) {
  if (!depStr || !retStr || depStr === "-" || retStr === "-") return null;
  const parse = (s) => {
    const t = normStr(s);
    const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const a = parse(depStr);
  const b = parse(retStr);
  if (!a || !b) return null;
  const days = Math.round((b.getTime() - a.getTime()) / 86400000);
  if (days <= 0 || days > 366) return null;
  return days;
}

function pickTourTitle(order, selectedResult) {
  const tryStr = (v, source) => {
    const s = normStr(v);
    return s ? { text: s, source } : null;
  };
  const fromSel =
    tryStr(selectedResult?.tour?.tourName, "selectedResult.tour.tourName") ||
    tryStr(selectedResult?.tour?.name, "selectedResult.tour.name") ||
    tryStr(selectedResult?.tour?.title, "selectedResult.tour.title") ||
    tryStr(selectedResult?.packageName, "selectedResult.packageName") ||
    tryStr(selectedResult?.deal?.tourName, "selectedResult.deal.tourName");
  if (fromSel) return fromSel;

  const ext = parseExtendedList(order);
  for (const row of ext) {
    const hit =
      tryStr(row?.tourName, "extended.tourName") ||
      tryStr(row?.tour?.name, "extended.tour.name") ||
      tryStr(row?.packageName, "extended.packageName");
    if (hit) return hit;
  }

  const deal = order?.deal && typeof order.deal === "object" ? order.deal : null;
  const fromDeal =
    tryStr(deal?.tourName, "deal.tourName") ||
    tryStr(deal?.name, "deal.name") ||
    tryStr(deal?.title, "deal.title");
  if (fromDeal) return fromDeal;

  const or = order?.order_response;
  const fromOr =
    tryStr(or?.tour?.tourName, "order_response.tour") ||
    tryStr(or?.packageName, "order_response.packageName");
  if (fromOr) return fromOr;

  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const fromOurPrice =
    tryStr(op?.packageTitle, "ourPrice.packageTitle") ||
    tryStr(op?.tourTitle, "ourPrice.tourTitle");
  if (fromOurPrice) return fromOurPrice;

  const nid = normStr(order?.dealid ?? order?.dealId);
  if (nid) return { text: `טיול מאורגן${nid ? ` · ${nid}` : ""}`, source: "dealId_fallback" };

  return { text: "טיול מאורגן", source: "generic_fallback" };
}

function pickDestinationLabel(order, selectedResult, resHotel) {
  const cityRaw =
    resHotel?.hotelCity ||
    order?.hotel?.hotelCity ||
    selectedResult?.hotel?.hotelCity ||
    selectedResult?.tour?.destination ||
    order?.destination;
  const c = normStr(cityRaw);
  if (c) {
    try {
      const tr = translateLDSDestinations(c);
      if (tr?.CITYNAME_HEB) return tr.CITYNAME_HEB;
    } catch {
      /* ignore */
    }
    return c;
  }
  const country = normStr(selectedResult?.tour?.country || order?.country);
  return country || "—";
}

function formatSupplierLine(order) {
  const si = order?.supplier_identity;
  if (si == null || si === "") return null;
  if (typeof si === "string") return normStr(si) || null;
  if (typeof si === "object") {
    const code = normStr(si.code ?? si.supplierCode ?? si.id);
    const name = normStr(si.name ?? si.label ?? si.title);
    if (name && code) return `${name} (${code})`;
    return name || code || null;
  }
  return null;
}

/** Bank-transfer copy for success header (OrganizedTour only). */
export const ORG_TOUR_BANK_TRANSFER_SUCCESS_SUBTITLE_HE =
  "הזמנת הטיול התקבלה ונשלחה לאישור ספק. ההזמנה ממתינה לאסמכתא תשלום בהעברה בנקאית.";

/**
 * @param {object|null} order
 * @param {object|null} selectedResult
 * @param {object|null} resHotel - reservation.hotel if any
 */
export function buildOrganizedTourPackageSuccessViewModel(order, selectedResult, resHotel) {
  const titlePick = pickTourTitle(order, selectedResult);
  const img = resolveOrganizedTourSuccessHeroImage(order, selectedResult);
  const pax = countPaxRooms(order);
  const depFromSel = selectedResult ? get_depret_from_result(selectedResult) : {};
  const depDate =
    normStr(order?.dep_date) ||
    normStr(order?.depDate) ||
    normStr(resHotel?.checkInDate) ||
    normStr(order?.hotel?.checkInDate) ||
    normStr(depFromSel?.depDate) ||
    "—";
  const retDate =
    normStr(order?.ret_date) ||
    normStr(order?.retDate) ||
    normStr(resHotel?.checkOutDate) ||
    normStr(order?.hotel?.checkOutDate) ||
    normStr(depFromSel?.retDate) ||
    "—";
  const nightsFromDates = nightsBetween(depDate, retDate);
  const nightsRaw =
    resHotel?.nights ??
    order?.hotel?.nights ??
    order?.nights ??
    nightsFromDates;
  const nights =
    nightsRaw != null && Number.isFinite(Number(nightsRaw)) && Number(nightsRaw) > 0
      ? String(Number(nightsRaw))
      : nightsFromDates != null
        ? String(nightsFromDates)
        : "—";
  const nightsNum = Number(nights);
  const daysLabel =
    Number.isFinite(nightsNum) && nightsNum > 0 ? `${nightsNum + 1} ימים` : "—";

  const destination = pickDestinationLabel(order, selectedResult, resHotel);
  const firstRoom = order?.passengersinfo?.[0];
  const boardResolved = resolveBoardBasisForPackageSummary(
    order,
    selectedResult,
    firstRoom?.room || null
  );
  const boardLabel = boardResolved.hasBoard ? boardResolved.boardLabel : null;
  const supplierLabel = formatSupplierLine(order);
  const hasRealHotel = hasMeaningfulHotelSnapshot(order, resHotel);

  return {
    heroImageUrl: img.url,
    imageSource: img.source,
    heroTitle: titlePick.text,
    titleSource: titlePick.source,
    destination,
    depDate,
    retDate,
    nights,
    daysLabel,
    passengerSummary: formatPaxSummary(pax),
    boardLabel,
    supplierLabel,
    hasRealHotel,
    roomsCount: Array.isArray(order?.passengersinfo) ? order.passengersinfo.length : 1,
  };
}
